import type { FulfillmentStrategy } from '@/config/uiConfig'
import { OrderStatusDisplay } from '@/types/NavigationTypes'

/**
 * Single source of truth for whether order editing is allowed in the UI.
 *
 * Today this governs editing indications/HPO terms; only "placed" and "submitted"
 * orders are editable. Once sample processing starts, the order becomes read-only.
 */
export function isOrderEditable(status?: OrderStatusDisplay | null): boolean {
  return status === OrderStatusDisplay.ORDER_PLACED || status === OrderStatusDisplay.ORDER_SUBMITTED
}

/**
 * Strategy-aware editability for indications/clinical fields.
 *
 * - rcigm (default): identical to :func:`isOrderEditable` — placed/submitted only.
 * - inflection_test: editable through accessioning (`SAMPLE_RECEIVED`) and after
 *   the final report (`RESULT_READY`); only `CANCELLED` (or null/unknown) is
 *   read-only. Mirrors the authoritative provider-api gate. Existing-member HPO
 *   editing is separately gated by the SeqOne HPO lock (see `canEditExistingMemberHpo`).
 */
export function isOrderEditableForStrategy(
  status: OrderStatusDisplay | null | undefined,
  strategy: FulfillmentStrategy
): boolean {
  if (strategy !== 'inflection_test') return isOrderEditable(status)
  if (status == null) return false
  return status !== OrderStatusDisplay.CANCELLED
}

export function isOrderReadOnly(status?: OrderStatusDisplay | null): boolean {
  return !isOrderEditable(status)
}

/**
 * SPA-side clinical-field editability for the edit-window response.
 * OPEN/QUEUED => editable; CLOSED/unknown => not. Mirrors the backend
 * (`editable` is authoritative; `windowState` is for display/observability).
 *
 * Defaults false so loading / error / missing responses all fail safe to
 * read-only — the SPA never optimistically enables editing.
 */
export function isEditWindowEditable(resp?: { editable?: boolean } | null): boolean {
  return resp?.editable === true
}
